import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import type { UserListItem } from '~/modules/settings/api/lib/schemas';
import { useUserList } from '~/modules/settings/api/queries/userList';
import { LabelGroups } from '~/modules/settings/components/LabelGroups';
import { useLabel } from '~settings/api/queries/label';
import { ContentBox } from '~settings/components/ContentBox';
import { Tabs } from '~settings/components/Tabs';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import {
  type LabelDetailTransformed,
  transformLabelDetails,
} from '~settings/utils/transformers';

import { DistributionTerritories } from './components/DistributionTerritories';
import { LabelAdmins } from './components/LabelAdmins';
import { LabelDetailsHeader } from './components/LabelDetailsHeader';
import { LabelMetadata } from './components/LabelMetadata';
import { Socials } from './components/Socials';
import { Sublabels } from './components/Sublabels';
import { Users } from './components/Users';

const userEmptyArray: UserListItem[] = [];

// Most of the search params are related to pagination, sorting, filtering, and search term.
// Those should be removed and kept only the ones that are necessary to keep the context of the page.
const SEARCH_PARAMS_TO_KEEP: string[] = [];

export function DetailLabelPage() {
  const { labelId } = useParams<{ labelId?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading, error, label } = useLabel<LabelDetailTransformed>(
    labelId || '',
    {
      select: transformLabelDetails,
    },
  );
  const { userList: adminUserList } = useUserList({
    roles: 'LABEL_ADMIN',
    labelIds: labelId,
  });

  const onTabChange = () => {
    for (const key of Array.from(searchParams.keys())) {
      if (!SEARCH_PARAMS_TO_KEEP.includes(key)) {
        searchParams.delete(key);
      }
    }

    setSearchParams(searchParams);
  };

  const isCurrentUserExternal = useIsExternalUser();

  useEffect(() => {
    if (!labelId) {
      navigate(SETTINGS_ROUTES.LABEL_LIST);
    }
  }, [labelId, navigate]);

  const tabs = useMemo(() => {
    if (!label) {
      return [];
    }

    return [
      {
        title: t('labelDetails.sublabelsCount', {
          sublabelsCount: label.sublabelsCount,
        }),
        component: <Sublabels parentLabelId={labelId} />,
      },
      {
        title: t('labelDetails.usersCount', {
          usersCount: label.usersCount,
        }),
        component: (
          <Users
            labelId={labelId}
            isSublabel={label.isSublabel}
            total={label.usersCount}
          />
        ),
      },
    ];
  }, [label, labelId, t]);

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !label) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <>
      <LabelDetailsHeader data={label} />
      <article className="flex gap-md py-md">
        <section className="inline-flex min-w-[19rem] flex-none basis-1/5 flex-col gap-md text-body-sm">
          <ContentBox>
            <LabelMetadata key="label-metadata" data={label} />
            {adminUserList?.data !== null && adminUserList?.data ? (
              <LabelAdmins
                key="label-admins-parent"
                adminUsers={adminUserList?.data ?? userEmptyArray}
              />
            ) : null}
            {!isCurrentUserExternal && label.distributionGroups !== null && (
              <DistributionTerritories
                key="distribution-territories"
                distributionGroups={label.distributionGroups}
              />
            )}
            {!isCurrentUserExternal && label.labelGroups.length !== 0 && (
              <LabelGroups key="label-groups" labelGroups={label.labelGroups} />
            )}
            {label.socials.length !== 0 && (
              <Socials key="socials-parent" socials={label.socials} />
            )}
          </ContentBox>
        </section>
        <ContentBox className="min-w-0 flex-1 rounded-sm py-lg">
          {label.parentId ? (
            <Users
              labelId={labelId}
              isSublabel={label.isSublabel}
              total={label.usersCount}
            />
          ) : (
            <Tabs tabs={tabs} onTabChange={onTabChange} />
          )}
        </ContentBox>
      </article>
    </>
  );
}
DetailLabelPage.displayName = 'DetailLabelPage';
